import React from 'react';
import type { FC } from 'react';
import kebabCase from 'lodash/kebabCase.js';
import { TextInput } from '@wix/cli-ui-kit';
import { getDashboardExternalPageExtensionsDir } from '@wix/cli-astro-definitions';
import { useProjectModel } from '../../../model.js';
import {
  composeValidations,
  createDashboardExternalPageUrlValidation,
  normalizeExternalPageUrl,
  pathAvailableValidation,
  pathValidation,
  useValidation,
} from '../../../dev-center/components/validations/index.js';
import { useWixCliAstroBi } from '../../../bi/index.js';
import { useWizardStep } from '../Wizard.js';

export const UrlStep: FC<{ onSubmit: (value: string) => void }> = ({
  onSubmit,
}) => {
  const bi = useWixCliAstroBi();
  const { isActive, goToNextStep } = useWizardStep();
  const validateUrl = useValidation(createDashboardExternalPageUrlValidation());

  return (
    <TextInput
      isActive={isActive}
      label={'External URL:'}
      validate={validateUrl}
      onSubmit={(url) => {
        const normalizedUrl = normalizeExternalPageUrl(url);

        bi.cliFlowStepAnswered({
          question: 'External URL:',
          questionKey: 'generate_command.dashboard_external_page.choose_url',
          answer: normalizedUrl,
          additionalInfo: { extensionType: 'dashboard external page' },
        });
        onSubmit(normalizedUrl);
        goToNextStep();
      }}
    />
  );
};

export const NameStep: FC<{
  name: string | undefined;
  onSubmit: (value: string) => void;
}> = ({ name, onSubmit }) => {
  const bi = useWixCliAstroBi();
  const { model } = useProjectModel();
  const { isActive, goToNextStep } = useWizardStep();

  const defaultName = name ?? kebabCase('external-page');

  const validateName = useValidation(
    composeValidations([
      [
        pathAvailableValidation(
          getDashboardExternalPageExtensionsDir(model.projectFolder)
        ),
        'The folder name must be unique. Select a name that has not been used yet in your application.',
      ],
      [
        pathValidation,
        'The folder name must be a valid path ([a-z0-9-/]+) and must not start with a dot.',
      ],
    ])
  );

  return (
    <TextInput
      isActive={isActive}
      label={'Enter the folder name for the new external page:'}
      placeholder={defaultName}
      onSubmit={(value) => {
        bi.cliFlowStepAnswered({
          question: 'Enter the folder name for the new external page:',
          questionKey: 'generate_command.dashboard_external_page.choose_name',
          answer: value,
          additionalInfo: { extensionType: 'dashboard external page' },
        });
        onSubmit(value);
        goToNextStep();
      }}
      validate={validateName}
    />
  );
};
